import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import TiptapEditor from '../../../components/TiptapEditor/TiptapEditor';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { contentService, CreatePageDto, UpdatePageDto } from '../../../services/content.service';
import { tenantService } from '../../../services/tenant.service';
import type { Tenant } from '../../../services/tenant.service';
import { CommonState } from '@erpgold/shared';

const { Title } = Typography;

export const PageForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const isEdit = Boolean(id);

  useEffect(() => {
    tenantService.getTenants({ limit: 100 })
      .then((res) => setTenants(res.data))
      .catch(() => { });
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    contentService.pages.getById(id)
      .then((page) => {
        form.setFieldsValue({
          title: page.title,
          slug: page.slug,
          sapo: page.sapo,
          description: page.description,
          tags: page.tags,
          state: page.state,
        });
      })
      .catch(() => message.error('Không tải được trang'))
      .finally(() => setLoading(false));
  }, [id, form]);

  const handleSubmit = async (values: CreatePageDto & UpdatePageDto) => {
    setSaving(true);
    try {
      if (isEdit && id) {
        await contentService.pages.update(id, values);
        message.success('Cập nhật trang thành công');
      } else {
        await contentService.pages.create(values);
        message.success('Tạo trang thành công');
      }
      navigate('/content/pages');
    } catch (error: unknown) {
      message.error((error as { message?: string })?.message || (isEdit ? 'Cập nhật thất bại' : 'Tạo thất bại'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/content/pages')}>
            Quay lại
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {isEdit ? 'Sửa trang' : 'Tạo trang mới'}
          </Title>
        </Space>
      </div>

      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={24}>
            <Col span={16}>
              <Card title="Nội dung" style={{ marginBottom: 16 }}>
                <Form.Item name="title" label="Tiêu đề" rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}>
                  <Input placeholder="Nhập tiêu đề trang" size="large" />
                </Form.Item>

                <Form.Item name="sapo" label="Sapo">
                  <Input.TextArea rows={3} placeholder="Mô tả ngắn" />
                </Form.Item>

                <Form.Item name="description" label="Nội dung" rules={[{ required: true, message: 'Vui lòng nhập nội dung' }]}>
                  <TiptapEditor placeholder="Nội dung đầy đủ" />
                </Form.Item>
              </Card>
            </Col>

            <Col span={8}>
              <Card title="Cài đặt" style={{ marginBottom: 16 }}>
                {!isEdit && (
                  <Form.Item name="tenant_id" label="Tenant" rules={[{ required: true, message: 'Vui lòng chọn tenant' }]}>
                    <Select
                      showSearch
                      placeholder="Chọn tenant"
                      options={tenants.map((t) => ({ label: `${t.name} (${t.id})`, value: t.id }))}
                    />
                  </Form.Item>
                )}

                <Form.Item name="state" label="Trạng thái">
                  <Select>
                    <Select.Option value={CommonState.ENABLED}>Hiển thị</Select.Option>
                    <Select.Option value={CommonState.DISABLED}>Ẩn</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item name="slug" label="Slug">
                  <Input placeholder="duong-dan-trang (tùy chọn)" />
                </Form.Item>

                <Form.Item name="tags" label="Tags">
                  <Select mode="tags" placeholder="Nhập tags" style={{ width: '100%' }} />
                </Form.Item>
              </Card>

              <Card>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={saving}
                    block
                    size="large"
                  >
                    {isEdit ? 'Lưu thay đổi' : 'Tạo trang'}
                  </Button>
                  <Button block onClick={() => navigate('/content/pages')}>
                    Hủy
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
        </Form>
      </Spin>
    </div>
  );
};

export default PageForm;
